/** @jsx h */
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { CloseIcon } from './yourListsIcons';

// Keep in sync with the CSS transition on .page--ld-confirm-overlay--fade.
const TRANSITION_MS = 250;

/**
 * Asks the user what to do with cart products that aren't part of the list
 * they just added: keep them, or remove them from the cart.
 *
 * `extras` is the array updateCart() returns (or null to hide). `onKeep`
 * dismisses; `onRemove` (async) removes the listed products. Fades in/out and
 * stays mounted through the exit transition.
 */
function CartExtrasModal({ extras, i18n = {}, onKeep, onRemove }) {
  const t = (key) => i18n[key] || key;
  const [rendered, setRendered] = useState(false);
  const [active, setActive] = useState(false);
  // Hold the last list so content stays during the exit transition.
  const [shown, setShown] = useState(extras);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (extras && extras.length) {
      setShown(extras);
      setBusy(false);
      setRendered(true);
      const raf = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(raf);
    }
    setActive(false);
    const timer = setTimeout(() => setRendered(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [extras]);

  if (!rendered) return null;
  const lines = shown || [];

  const handleRemove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onRemove();
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <div
      className={`page--ld-confirm-overlay page--ld-confirm-overlay--fade${
        active ? ' is-open' : ''
      }`}
      role="presentation"
      onClick={busy ? undefined : onKeep}
    >
      <div
        className="page--ld-confirm"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="page--ld-confirm-close"
          aria-label={t('cancel')}
          disabled={busy}
          onClick={onKeep}
        >
          <CloseIcon />
        </button>

        <div className="page--ld-confirm-title">{t('cart_extras_title')}</div>
        <div className="page--ld-confirm-subtitle">{t('cart_extras_subtitle')}</div>

        <ul className="page--ld-extras">
          {lines.map((l) => (
            <li key={l.id} className="page--ld-extras-item">
              <span className="page--ld-extras-media">
                {l.image ? (
                  <img src={l.image} alt={l.title} />
                ) : (
                  <span className="page--ld-extras-media--placeholder" aria-hidden="true" />
                )}
              </span>
              <span className="page--ld-extras-title">{l.title}</span>
              <span className="page--ld-extras-qty">&times;{l.quantity}</span>
            </li>
          ))}
        </ul>

        <div className="page--ld-confirm-actions">
          <button
            type="button"
            className="page--ld-confirm-delete"
            disabled={busy}
            onClick={handleRemove}
          >
            {busy ? t('cart_extras_removing') : t('cart_extras_remove')}
          </button>
          <button
            type="button"
            className="page--ld-confirm-cancel"
            disabled={busy}
            onClick={onKeep}
          >
            {t('cart_extras_keep')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CartExtrasModal;
